import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Copy, ExternalLink, QrCode, Download, MessageCircle, Globe, Key, Plus, Trash2, UtensilsCrossed, BedDouble, ImageIcon, Upload } from "lucide-react";

export default function Settings() {
  const { business, setBusiness } = useAuth();
  const [f, setF] = useState(business || {});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (business) setF(business); }, [business]);

  const publicUrl = `${window.location.origin}/p/${business?.slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(publicUrl)}`;

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.post("/business", f); setBusiness(data); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiato negli appunti"); };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/business/photo", fd);
      const fullUrl = `${BACKEND_URL}${data.url}`;
      const updated = { ...f, photo: fullUrl };
      setF(updated);
      const res = await api.post("/business", updated);
      setBusiness(res.data);
      toast.success("Foto attività aggiornata ✅");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const removePhoto = async () => {
    const updated = { ...f, photo: "" };
    setF(updated);
    try { const res = await api.post("/business", updated); setBusiness(res.data); toast.success("Foto rimossa"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const isSlot = f.mode === "slot";
  const resources = f.resources || [];
  const services = f.services || [];
  const resourceLabel = isSlot ? "tavolo" : "camera";

  const addResource = () => setF((p) => ({ ...p, resources: [...(p.resources || []), { name: `${isSlot ? "Tavolo" : "Camera"} ${(p.resources || []).length + 1}`, capacity: 2 }] }));
  const rmResource = (i) => setF((p) => ({ ...p, resources: (p.resources || []).filter((_, idx) => idx !== i) }));
  const setResource = (i, k, v) => setF((p) => ({ ...p, resources: (p.resources || []).map((r, idx) => idx === i ? { ...r, [k]: k === "capacity" ? Number(v) : v } : r) }));
  const addService = () => setF((p) => ({ ...p, services: [...(p.services || []), { name: "Servizio", start: "12:00", end: "14:30" }] }));
  const rmService = (i) => setF((p) => ({ ...p, services: (p.services || []).filter((_, idx) => idx !== i) }));
  const setService = (i, k, v) => setF((p) => ({ ...p, services: (p.services || []).map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-primary">Impostazioni</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Attività e collegamenti</h1>
      </div>

      {/* Hero photo */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="settings-photo-section">
        <div className="flex items-center gap-2 mb-4"><ImageIcon className="h-5 w-5 text-primary" /><p className="font-heading font-bold text-slate-900">Foto attività (hero)</p></div>
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-full sm:w-64 aspect-video rounded-xl overflow-hidden bg-muted border border-slate-200 grid place-items-center shrink-0">
            {f.photo ? (
              <img src={f.photo} alt="Foto attività" className="w-full h-full object-cover" data-testid="settings-photo-preview" />
            ) : (
              <div className="text-center text-slate-400 p-4"><ImageIcon className="h-8 w-8 mx-auto mb-1" /><p className="text-xs">Nessuna foto — verrà usata un'immagine di default</p></div>
            )}
          </div>
          <div className="flex-1 space-y-3">
            <p className="text-sm text-slate-600">Questa foto appare in cima alla tua pagina pubblica di prenotazione. Carica un'immagine dal telefono o dal PC (JPG, PNG o WEBP, max 5MB).</p>
            <div className="flex flex-wrap gap-2">
              <label className={`inline-flex items-center gap-2 rounded-xl bg-primary text-white px-4 py-2.5 text-sm font-medium cursor-pointer hover:bg-primary/90 ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="settings-photo-upload-label">
                <Upload className="h-4 w-4" /> {uploading ? "Caricamento..." : f.photo ? "Cambia foto" : "Carica foto"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadPhoto} data-testid="settings-photo-input" />
              </label>
              {f.photo && (
                <Button variant="outline" onClick={removePhoto} className="rounded-xl text-destructive" data-testid="settings-photo-remove"><Trash2 className="h-4 w-4 mr-1" /> Rimuovi</Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Public link + QR */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4"><Globe className="h-5 w-5 text-primary" /><p className="font-heading font-bold text-slate-900">Pagina pubblica di prenotazione</p></div>
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="flex-1 space-y-3">
            <div>
              <Label>Link pubblico</Label>
              <div className="flex gap-2 mt-1">
                <Input value={publicUrl} readOnly className="text-xs" data-testid="settings-public-url" />
                <Button variant="outline" size="icon" onClick={() => copy(publicUrl)} data-testid="settings-copy-url"><Copy className="h-4 w-4" /></Button>
                <a href={publicUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="icon"><ExternalLink className="h-4 w-4" /></Button></a>
              </div>
            </div>
            <p className="text-xs text-slate-500">Condividi questo link o il QR sui tavoli / in reception. I clienti prenotano da soli e ricevono conferma WhatsApp.</p>
            <a href={qrUrl} download="qr-prenotazioni.png"><Button variant="outline" size="sm" className="rounded-lg" data-testid="settings-download-qr"><Download className="h-4 w-4 mr-1" /> Scarica QR</Button></a>
          </div>
          <div className="grid place-items-center">
            <div className="rounded-xl border border-slate-200 p-3 bg-white">
              <img src={qrUrl} alt="QR" width={160} height={160} data-testid="settings-qr-image" />
              <p className="text-center text-[11px] text-slate-400 mt-1 flex items-center justify-center gap-1"><QrCode className="h-3 w-3" /> Scansiona per prenotare</p>
            </div>
          </div>
        </div>
      </div>

      {/* Portal key */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><Key className="h-5 w-5 text-primary" /><p className="font-heading font-bold text-slate-900">Collegamento portale</p></div>
        <p className="text-sm text-slate-600 mb-2">Slug attività per l'instradamento dei lead da turismo.latina.it:</p>
        <div className="flex gap-2">
          <Input value={business?.slug || ""} readOnly data-testid="settings-slug" />
          <Button variant="outline" size="icon" onClick={() => copy(business?.slug)}><Copy className="h-4 w-4" /></Button>
        </div>
        <a href="/portal-demo" target="_blank" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-3" data-testid="settings-portal-demo-link">
          <ExternalLink className="h-4 w-4" /> Apri la demo del portale (genera un lead di test)
        </a>
      </div>

      {/* Resources & rules */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="settings-resources-section">
        <div className="flex items-center gap-2">
          {isSlot ? <UtensilsCrossed className="h-5 w-5 text-primary" /> : <BedDouble className="h-5 w-5 text-primary" />}
          <p className="font-heading font-bold text-slate-900">{isSlot ? "Tavoli e fasce orarie" : "Camere e regole soggiorno"}</p>
        </div>

        <div className="space-y-2">
          <Label>{isSlot ? "Tavoli" : "Camere"}</Label>
          {resources.length === 0 && <p className="text-sm text-slate-400">Nessun {resourceLabel} configurato.</p>}
          {resources.map((r, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input value={r.name} onChange={(e) => setResource(i, "name", e.target.value)} className="flex-1" data-testid={`settings-resource-name-${i}`} />
              <div className="flex items-center gap-1">
                <Input type="number" min={1} value={r.capacity} onChange={(e) => setResource(i, "capacity", e.target.value)} className="w-20" data-testid={`settings-resource-cap-${i}`} />
                <span className="text-xs text-slate-400 w-12">posti</span>
              </div>
              <button onClick={() => rmResource(i)} className="p-2 text-destructive" data-testid={`settings-resource-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addResource} className="rounded-lg" data-testid="settings-add-resource"><Plus className="h-4 w-4 mr-1" /> Aggiungi {resourceLabel}</Button>
        </div>

        {isSlot ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min coperti</Label><Input type="number" min={1} value={f.min_guests ?? 1} onChange={(e) => set("min_guests", Number(e.target.value))} data-testid="settings-min-guests" /></div>
              <div><Label>Max coperti</Label><Input type="number" min={1} value={f.max_guests ?? 12} onChange={(e) => set("max_guests", Number(e.target.value))} data-testid="settings-max-guests" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Intervallo slot (min)</Label><Input type="number" min={5} value={f.slot_interval ?? 30} onChange={(e) => set("slot_interval", Number(e.target.value))} data-testid="settings-slot-interval" /></div>
              <div><Label>Durata tavolo (min)</Label><Input type="number" min={15} value={f.turn_duration ?? 90} onChange={(e) => set("turn_duration", Number(e.target.value))} data-testid="settings-turn-duration" /></div>
            </div>
            <div className="space-y-2">
              <Label>Fasce di servizio</Label>
              {services.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={s.name} onChange={(e) => setService(i, "name", e.target.value)} className="flex-1" data-testid={`settings-service-name-${i}`} />
                  <Input type="time" value={s.start} onChange={(e) => setService(i, "start", e.target.value)} className="w-32" data-testid={`settings-service-start-${i}`} />
                  <Input type="time" value={s.end} onChange={(e) => setService(i, "end", e.target.value)} className="w-32" data-testid={`settings-service-end-${i}`} />
                  <button onClick={() => rmService(i)} className="p-2 text-destructive" data-testid={`settings-service-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addService} className="rounded-lg" data-testid="settings-add-service"><Plus className="h-4 w-4 mr-1" /> Aggiungi fascia</Button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min ospiti</Label><Input type="number" min={1} value={f.min_guests ?? 1} onChange={(e) => set("min_guests", Number(e.target.value))} data-testid="settings-min-guests" /></div>
              <div><Label>Max ospiti</Label><Input type="number" min={1} value={f.max_guests ?? 12} onChange={(e) => set("max_guests", Number(e.target.value))} data-testid="settings-max-guests" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Min notti</Label><Input type="number" min={1} value={f.min_nights ?? 1} onChange={(e) => set("min_nights", Number(e.target.value))} data-testid="settings-min-nights" /></div>
              <div><Label>Check-in</Label><Input type="time" value={f.checkin_time || "15:00"} onChange={(e) => set("checkin_time", e.target.value)} data-testid="settings-checkin-time" /></div>
              <div><Label>Check-out</Label><Input type="time" value={f.checkout_time || "10:00"} onChange={(e) => set("checkout_time", e.target.value)} data-testid="settings-checkout-time" /></div>
            </div>
          </>
        )}
        <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="settings-save-resources-btn">{saving ? "Salvataggio..." : `Salva ${isSlot ? "tavoli e fasce" : "camere e regole"}`}</Button>
      </div>

      {/* Notifications + general */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <p className="font-heading font-bold text-slate-900">Preferenze</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Nome attività</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="settings-name-input" /></div>
          <div><Label>Telefono</Label><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
        </div>
        <div><Label>Indirizzo</Label><Input value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
        <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 p-3">
          <span className="flex items-center gap-2 text-sm font-medium text-slate-800"><MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp automatico (Twilio)</span>
          <Switch checked={!!f.whatsapp_enabled} onCheckedChange={(v) => set("whatsapp_enabled", v)} data-testid="settings-whatsapp-switch" />
        </div>
        <div className="flex items-center justify-between rounded-xl bg-muted p-3">
          <span className="text-sm font-medium text-slate-800">Email di backup</span>
          <Switch checked={!!f.email_enabled} onCheckedChange={(v) => set("email_enabled", v)} data-testid="settings-email-switch" />
        </div>
        <div className="flex items-center justify-between rounded-xl bg-muted p-3">
          <span className="text-sm font-medium text-slate-800">Auto-conferma prenotazioni</span>
          <Switch checked={!!f.auto_confirm} onCheckedChange={(v) => set("auto_confirm", v)} data-testid="settings-autoconfirm-switch" />
        </div>
        <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="settings-save-btn">{saving ? "Salvataggio..." : "Salva impostazioni"}</Button>
      </div>
    </div>
  );
}
